import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, GitBranch, Send, DollarSign, Megaphone, Wallet, Ban, Building2, BookMarked,
} from "lucide-react";

const REL_LABEL = {
  visitor: "Visitors", registered_household: "Registered households",
  advisor_prospect: "Advisor prospects", active_client: "Active clients",
};
const REL_TONE = {
  visitor: "text-[#64748B]", registered_household: "text-[#1E3E62]",
  advisor_prospect: "text-[#92400E]", active_client: "text-[#166534]",
};

export default function HelmAgencyOverview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/agent/agency-overview").then(({ data }) => setD(data))
      .catch((e) => setErr(e?.response?.status === 403 ? "Owner access only." : "Could not load overview."));
  }, []);

  if (err) return <div className="p-6 sm:p-10 max-w-5xl mx-auto"><div className="rounded-xl border-2 border-[#F3D6D6] bg-[#FDF3F3] p-8 text-center text-[#991B1B] font-semibold" data-testid="agency-overview-error">{err}</div></div>;
  if (!d) return <div className="p-6 sm:p-10 max-w-5xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-6" data-testid="agency-overview">
      <div>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#D4AF37]"><Building2 className="w-4 h-4" />Agency Overview</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight mt-1" data-testid="agency-overview-title">The whole agency, at a glance</h1>
        <p className="text-muted-foreground mt-1.5">Owner view — distinct from an advisor's desk and the Roster. Only measures backed by real data are shown; the rest are marked unavailable with the source needed to unlock them.</p>
      </div>

      {/* totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat label="Households" value={d.totals.households} icon={Users} />
        <Stat label="Imported SBA clients" value={d.totals.imported_sba_clients} icon={Building2} />
        <Stat label="Carrier-confirmed enrollments" value={d.production.households.enrollment_confirmed} icon={Send} tone="text-[#166534]" />
      </div>

      {/* pipeline */}
      <Panel title="Pipeline — relationship" icon={GitBranch} note={d.pipeline.note} testid="agency-pipeline">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(d.pipeline.relationship).map(([k, v]) => (
            <div key={k} className="rounded-lg border-2 border-[#E5E5E0] bg-white p-3" data-testid={`pipeline-${k}`}>
              <p className={`text-2xl font-bold ${REL_TONE[k]}`}>{v}</p>
              <p className="text-xs text-[#6B7280]">{REL_LABEL[k] || k}</p>
            </div>
          ))}
        </div>
      </Panel>

      {/* workload */}
      <Panel title="Workload — assignments" icon={Users} note={d.workload.note} testid="agency-workload">
        {d.workload.unassigned > 0 && (
          <div className="rounded-lg border-2 border-[#FDE68A] bg-[#FFFBEB] p-3 mb-3 text-sm text-[#92400E]" data-testid="workload-unassigned">
            <b>{d.workload.unassigned}</b> unassigned households.
          </div>
        )}
        <div className="rounded-lg border-2 border-[#E5E5E0] bg-white divide-y">
          {d.workload.producers.map((p) => (
            <div key={p.producer_id} className="flex items-center gap-3 p-3" data-testid={`workload-producer-${p.producer_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0B192C] truncate">{p.name}</p>
                <p className="text-xs text-[#9CA3AF]">Writing NPN {p.npn || "—"} · payee <span className="italic">unavailable</span></p>
              </div>
              <span className="text-sm font-bold text-[#0B192C]">{p.assigned}</span>
            </div>
          ))}
        </div>
      </Panel>

      {/* production */}
      <Panel title="Production — evidence-based" icon={Send} note={d.production.note} testid="agency-production">
        <div className="grid grid-cols-3 gap-3">
          <ProdStat label="Links opened" value={d.production.households.link_opened} sub={`${d.production.events.link_opened} events`} tone="text-[#1E3E62]" k="link_opened" />
          <ProdStat label="Applications submitted" value={d.production.households.application_submitted} sub={`${d.production.events.application_submitted} events`} tone="text-[#92400E]" k="application_submitted" />
          <ProdStat label="Carrier-confirmed" value={d.production.households.enrollment_confirmed} sub={`${d.production.events.enrollment_confirmed} events`} tone="text-[#166534]" k="enrollment_confirmed" />
        </div>
      </Panel>

      {/* writing-agent book */}
      {d.book?.available && (
        <Panel title="Writing-agent book" icon={BookMarked} note={d.book.note} testid="agency-book">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <ProdStat label="Client households" value={d.book.totals.distinct_client_households} sub="distinct" tone="text-[#0B192C]" k="book-households" />
            <ProdStat label="Existing book" value={d.book.totals.existing_book} sub={`${d.book.totals.new_production} new production`} tone="text-[#1E3E62]" k="book-existing" />
            <ProdStat label="Effective / submitted" value={`${d.book.totals.effective} / ${d.book.totals.submitted}`} sub="active vs submitted" tone="text-[#166534]" k="book-states" />
          </div>
          {d.book.by_writing_producer.length === 0 ? (
            <p className="text-sm text-[#6B7280]" data-testid="agency-book-empty">No attested book yet. Advisors attest "business I wrote" on a household's Coverage tab.</p>
          ) : (
            <div className="rounded-lg border-2 border-[#E5E5E0] bg-white divide-y" data-testid="agency-book-producers">
              {d.book.by_writing_producer.map((p) => (
                <div key={p.writing_producer_id} className="flex items-center gap-3 p-3" data-testid={`book-producer-${p.writing_producer_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#0B192C] truncate">{p.name || "—"}</p>
                    <p className="text-xs text-[#9CA3AF]">{p.agency || "Agency not on producer record"} · {p.distinct_client_households} households · {p.existing_book} existing / {p.new_production} new</p>
                  </div>
                  <span className="text-sm font-bold text-[#0B192C]">{p.policies}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {/* unavailable measures */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Unavailable title="Campaigns" icon={Megaphone} reason={d.campaigns.reason} />
        <Unavailable title="Revenue" icon={DollarSign} reason={d.revenue.reason} />
        <Unavailable title="Compensation (indep. vs LOA)" icon={Wallet} reason={d.compensation.reason} />
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "text-[#0B192C]" }) {
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 text-[#9CA3AF] mb-1"><Icon className="w-4 h-4" /><span className="text-xs font-semibold uppercase tracking-wide">{label}</span></div>
      <p className={`text-3xl font-bold ${tone}`}>{value}</p>
    </div>
  );
}

function ProdStat({ label, value, sub, tone, k }) {
  return (
    <div className="rounded-lg border-2 border-[#E5E5E0] bg-white p-3" data-testid={`production-${k}`}>
      <p className={`text-2xl font-bold ${tone}`}>{value}</p>
      <p className="text-xs font-semibold text-[#0B192C]">{label}</p>
      <p className="text-[11px] text-[#9CA3AF]">{sub}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, note, children, testid }) {
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-[#FBFBF9] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3"><Icon className="w-5 h-5 text-[#0B192C]" /><h2 className="text-lg font-bold text-[#0B192C]">{title}</h2></div>
      {children}
      {note && <p className="text-[11px] text-[#6B7280] mt-3">{note}</p>}
    </div>
  );
}

function Unavailable({ title, icon: Icon, reason }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-[#E5E5E0] bg-white p-4 opacity-90" data-testid={`unavailable-${title}`}>
      <div className="flex items-center gap-2 text-[#9CA3AF] mb-1"><Icon className="w-4 h-4" /><span className="text-sm font-bold text-[#374151]">{title}</span></div>
      <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#92400E] bg-[#FEF3C7] rounded-full px-2 py-0.5 my-1"><Ban className="w-3 h-3" />Unavailable</div>
      <p className="text-xs text-[#6B7280]">{reason}</p>
    </div>
  );
}
